import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Modal, PasswordInput, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { isApiError } from '@/services/httpClient';
import { useStore } from '@/store/Store';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import type { IProduct } from '@/types/Product.model';
import {
  loadAnnotationLinkedProduct,
  unlockAndLoadAnnotationLinkedProduct,
} from '@/utils/annotationLinkedProduct';

type GateState = {
  productId: string;
  productName?: string;
  mode: 'password' | 'restricted';
};

/**
 * Opens a linked annotation product with link-share gates (password / restricted).
 */
export function useAnnotationLinkedProduct(onOpen: (product: IProduct) => void) {
  const { t } = useTranslation();
  const accentColor =
    useStore((state) => state.userProfile.qrCodeColor) || DEFAULT_QR_CODE_COLOR;
  const [gate, setGate] = useState<GateState | null>(null);
  const [password, setPassword] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);

  const closeGate = useCallback(() => {
    setGate(null);
    setPassword('');
    setUnlockError(null);
    setUnlockLoading(false);
  }, []);

  const openByProductId = useCallback(
    async (productId: string) => {
      const result = await loadAnnotationLinkedProduct(productId);
      if (result.status === 'success') {
        onOpen(result.product);
        return;
      }
      if (result.status === 'password_required') {
        setGate({
          productId,
          productName: result.locked.productName,
          mode: 'password',
        });
        setPassword('');
        setUnlockError(null);
        return;
      }
      if (result.status === 'restricted') {
        setGate({
          productId,
          productName: result.locked.productName,
          mode: 'restricted',
        });
        return;
      }
      notifications.show({
        title: t('editProduct.error'),
        message: t('editProduct.annotationProductLoadError'),
        color: 'red',
      });
    },
    [onOpen, t]
  );

  const handleUnlock = useCallback(async () => {
    if (!gate || gate.mode !== 'password') return;
    setUnlockLoading(true);
    setUnlockError(null);
    try {
      const result = await unlockAndLoadAnnotationLinkedProduct(
        gate.productId,
        password
      );
      if (result.status === 'success') {
        closeGate();
        onOpen(result.product);
        return;
      }
      if (result.status === 'password_required') {
        setUnlockError(t('productViewer.linkShare.wrongPassword'));
        return;
      }
      setUnlockError(t('productViewer.linkShare.unlockFailed'));
    } catch (e: unknown) {
      if (isApiError(e) && e.response.status === 401) {
        setUnlockError(t('productViewer.linkShare.wrongPassword'));
      } else {
        setUnlockError(t('productViewer.linkShare.unlockFailed'));
      }
    } finally {
      setUnlockLoading(false);
    }
  }, [gate, password, closeGate, onOpen, t]);

  const accessModal = gate ? (
    <Modal
      opened
      onClose={closeGate}
      closeOnClickOutside={false}
      classNames={{ root: 'annotation-link-gate-modal' }}
      title={
        gate.mode === 'password'
          ? t('editProduct.annotationProductPasswordTitle')
          : t('editProduct.annotationProductRestrictedTitle')
      }
      centered
      size="sm"
      zIndex={400}
    >
      <Text size="sm" c="dimmed" mb="md">
        {gate.mode === 'password'
          ? gate.productName
            ? t('editProduct.annotationProductPasswordHintNamed', {
                name: gate.productName,
              })
            : t('editProduct.annotationProductPasswordHint')
          : gate.productName
            ? t('editProduct.annotationProductRestrictedHintNamed', {
                name: gate.productName,
              })
            : t('editProduct.annotationProductRestrictedHint')}
      </Text>
      {gate.mode === 'password' ? (
        <>
          <PasswordInput
            label={t('productViewer.linkShare.passwordLabel')}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            mb="sm"
            autoComplete="current-password"
          />
          {unlockError ? (
            <Text size="sm" c="red" mb="sm">
              {unlockError}
            </Text>
          ) : null}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeGate}>
              {t('editProduct.cancel')}
            </Button>
            <Button
              color={accentColor}
              loading={unlockLoading}
              onClick={() => void handleUnlock()}
            >
              {t('productViewer.linkShare.unlock')}
            </Button>
          </Group>
        </>
      ) : (
        <Group justify="flex-end" mt="md">
          <Button color={accentColor} onClick={closeGate}>
            {t('editProduct.annotationProductDismiss')}
          </Button>
        </Group>
      )}
    </Modal>
  ) : null;

  return { openByProductId, accessModal };
}
